import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import RichEditor from '../util/RichEditor';
import { TrainingCourse } from './Training';
import { useSnackbar } from 'notistack';
import SelectUsers from './SelectUsers';

// ── Types ─────────────────────────────────────────────────────────────────────

interface AnswerDraft {
  localId: string;
  text: string;
  isCorrect: boolean;
}

interface QuestionDraft {
  localId: string;
  serverId?: number;
  text: string;
  type: 'single' | 'multiple' | 'true_false';
  answers: AnswerDraft[];
}

interface PageDraft {
  localId: string;
  serverId?: number;
  type: 'content' | 'quiz';
  title: string;
  content: string;
  questions: QuestionDraft[];
  order: number;
  passThreshold: number;
}

interface Props {
  course: TrainingCourse | null;
  onClose: () => void;
  onSaved: () => void;
  baseUrl: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

let _lid = 0;
const lid = () => `l${++_lid}`;

const RETAKE_OPTIONS: { label: string; days: number | null }[] = [
  { label: 'No retake',  days: null },
  { label: '6 months',   days: 182 },
  { label: '1 year',     days: 365 },
  { label: '2 years',    days: 730 },
];

const UNIT_DAYS: Record<'days' | 'weeks' | 'months' | 'years', number> = {
  days: 1, weeks: 7, months: 30, years: 365,
};

const EMOJIS = [
  // Security & compliance
  '🔒','🔐','🔑','🛡','⚖️','📋','📜','✅','🚫','⚠️','🔏','🕵️','🆔','📑',
  // Safety & emergency
  '🧯','🚨','⛑','🦺','🚒','🩹','☢️','☣️','🔥','💥','🚪','🪜',
  // IT & cyber
  '💻','🖥','🖱','⌨️','📱','🌐','🔌','💾','🐛','🦠','📡','⚙️',
  // Health & wellbeing
  '🏥','🩺','💊','🧠','🫀','😷','🧘','💪','🥗','💤','🚑',
  // People & HR
  '🤝','👥','🧑‍💼','👔','🗣','🎓','🏆','🌍','♿','🫶','📣',
  // Finance & business
  '💰','💳','📊','📈','📉','💼','🧾','🏦','💵','🪙','📦',
  // General / learning
  '📚','📖','📝','💡','🎯','🚀','⭐','🔔','📌','🗂','🧩','🔍','🎨','🛠','🏗','🚗','✈️','🍽','🌱','♻️','⏰','📅','🎉','🔬','🧪','📐','🗺','🏭',
];

const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ header: 2 }, { header: 3 }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};
const QUILL_FORMATS = ['bold','italic','underline','header','list','bullet','link'];

// ── CourseBuilder ─────────────────────────────────────────────────────────────

const CourseBuilder: React.FC<Props> = ({ course, onClose, onSaved, baseUrl }) => {
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!course?.id;

  // course metadata
  const [courseId, setCourseId]           = useState<number | null>(course?.id ?? null);
  const [title, setTitle]                 = useState(course?.title ?? '');
  const [description, setDescription]    = useState(course?.description ?? '');
  const [emoji, setEmoji]                 = useState('📚');
  const [passThreshold, setPassThreshold] = useState(course?.passThreshold ?? 80);
  const [retakeDays, setRetakeDays]       = useState<number | null>(course?.retakeIntervalDays ?? null);
  const [customUnit, setCustomUnit]       = useState<'days' | 'weeks' | 'months' | 'years'>('days');
  const [customNum, setCustomNum]         = useState<string>('');
  const [isMandatory, setIsMandatory]     = useState<boolean>((course as any)?.isMandatory ?? false);
  const [isForAll, setIsForAll]           = useState(course?.isForAll ?? true);
  const [assignedUserIds, setAssignedUserIds] = useState<number[]>(
    (course as any)?.users?.map((u: any) => u.id) ?? [],
  );

  // pages
  const [pages, setPages]   = useState<PageDraft[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activePanel, setActivePanel] = useState<'settings' | 'page'>('settings');

  // ui state
  const [saving, setSaving]     = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [open, setOpen]         = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showIconGrid, setShowIconGrid] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dropIdx, setDropIdx] = useState<number | null>(null);
  const addMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => { requestAnimationFrame(() => setOpen(true)); }, []);

  // Seed custom retake fields if the course has a non-preset interval
  useEffect(() => {
    const d = course?.retakeIntervalDays ?? null;
    if (d !== null && !RETAKE_OPTIONS.some(o => o.days === d)) {
      setCustomNum(String(d));
      setCustomUnit('days');
    }
  }, [course?.retakeIntervalDays]);

  // Load existing course — each question gets its own quiz page (mirrors CourseViewer display)
  useEffect(() => {
    if (!isEdit || !course?.id) return;
    axios.get(`training/course/${course.id}`).then(({ data }) => {
      const contentPages: PageDraft[] = (data.pages ?? []).map((p: any): PageDraft => ({
        localId: lid(), serverId: p.id, type: 'content',
        title: p.title, content: p.content ?? '', questions: [], order: p.order, passThreshold: 80,
      }));
      const quizPages: PageDraft[] = (data.questions ?? []).map((q: any): PageDraft => ({
        localId: lid(), serverId: undefined, type: 'quiz',
        title: '', content: '', order: q.order ?? 999,
        passThreshold: course?.passThreshold ?? 80,
        questions: [{
          localId: lid(), serverId: q.id, text: q.text,
          type: q.type ?? 'single',
          answers: (q.answers ?? []).map((a: any): AnswerDraft => ({
            localId: lid(), text: a.text, isCorrect: a.isCorrect ?? false,
          })),
        }],
      }));
      const loadedPages = [...contentPages, ...quizPages].sort((a, b) => a.order - b.order);
      setPages(loadedPages);
      if (loadedPages.length > 0) {
        setActiveId(loadedPages[0].localId);
        setActivePanel('page');
      }
    }).catch(() => {});
  }, [isEdit, course?.id, course?.passThreshold]);

  // Close on Escape
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setTimeout(onClose, 300);
  }, [onClose]);

  // ── Page operations ──────────────────────────────────────────────────────────

  const addPage = (type: 'content' | 'quiz') => {
    const newPage: PageDraft = {
      localId: lid(), type, title: type === 'quiz' ? 'Knowledge Check' : 'New page',
      content: '', order: pages.length, passThreshold: passThreshold,
      questions: type === 'quiz' ? [{
        localId: lid(), text: '', type: 'single',
        answers: [
          { localId: lid(), text: '', isCorrect: false },
          { localId: lid(), text: '', isCorrect: false },
        ],
      }] : [],
    };
    setPages(prev => [...prev, newPage]);
    setActiveId(newPage.localId);
    setActivePanel('page');
    setShowAddMenu(false);
  };

  const deletePage = (localId: string) => {
    setPages(prev => prev.filter(p => p.localId !== localId));
    if (activeId === localId) {
      const remaining = pages.filter(p => p.localId !== localId);
      setActiveId(remaining[0]?.localId ?? null);
      setActivePanel(remaining.length > 0 ? 'page' : 'settings');
    }
  };

  const updatePage = (localId: string, patch: Partial<PageDraft>) => {
    setPages(prev => prev.map(p => p.localId === localId ? { ...p, ...patch } : p));
  };

  // ── Question operations ───────────────────────────────────────────────────────

  // Adds a new quiz page (= one question) immediately after the given page
  const addQuestion = (afterPageLocalId: string) => {
    const newPage: PageDraft = {
      localId: lid(), type: 'quiz', title: '', content: '',
      order: 0, passThreshold: passThreshold,
      questions: [{
        localId: lid(), text: '', type: 'single',
        answers: [
          { localId: lid(), text: '', isCorrect: false },
          { localId: lid(), text: '', isCorrect: false },
        ],
      }],
    };
    setPages(prev => {
      const idx = prev.findIndex(p => p.localId === afterPageLocalId);
      const next = [...prev];
      next.splice(idx === -1 ? next.length : idx + 1, 0, newPage);
      return next;
    });
    setActiveId(newPage.localId);
    setActivePanel('page');
  };

  const updateQuestion = (pageLocalId: string, qLocalId: string, patch: Partial<QuestionDraft>) => {
    setPages(prev => prev.map(p => {
      if (p.localId !== pageLocalId) return p;
      return { ...p, questions: p.questions.map(q => q.localId === qLocalId ? { ...q, ...patch } : q) };
    }));
  };

  const deleteQuestion = (pageLocalId: string, qLocalId: string) => {
    setPages(prev => prev.map(p => {
      if (p.localId !== pageLocalId) return p;
      return { ...p, questions: p.questions.filter(q => q.localId !== qLocalId) };
    }));
  };

  // Switch a question between single- and multiple-correct.
  // When going back to single, keep only the first marked answer correct.
  const setQuestionType = (pageLocalId: string, qLocalId: string, type: QuestionDraft['type']) => {
    setPages(prev => prev.map(p => {
      if (p.localId !== pageLocalId) return p;
      return { ...p, questions: p.questions.map(q => {
        if (q.localId !== qLocalId) return q;
        let answers = q.answers;
        if (type !== 'multiple') {
          let kept = false;
          answers = q.answers.map(a => {
            if (a.isCorrect && !kept) { kept = true; return a; }
            return { ...a, isCorrect: false };
          });
        }
        return { ...q, type, answers };
      })};
    }));
  };

  const addOption = (pageLocalId: string, qLocalId: string) => {
    setPages(prev => prev.map(p => {
      if (p.localId !== pageLocalId) return p;
      return { ...p, questions: p.questions.map(q => {
        if (q.localId !== qLocalId) return q;
        return { ...q, answers: [...q.answers, { localId: lid(), text: '', isCorrect: false }] };
      })};
    }));
  };

  const toggleCorrect = (pageLocalId: string, qLocalId: string, aLocalId: string) => {
    setPages(prev => prev.map(p => {
      if (p.localId !== pageLocalId) return p;
      return { ...p, questions: p.questions.map(q => {
        if (q.localId !== qLocalId) return q;
        const isSingle = q.type === 'single' || q.type === 'true_false';
        return { ...q, answers: q.answers.map(a => ({
          ...a,
          isCorrect: isSingle ? a.localId === aLocalId : (a.localId === aLocalId ? !a.isCorrect : a.isCorrect),
        }))};
      })};
    }));
  };

  const updateAnswer = (pageLocalId: string, qLocalId: string, aLocalId: string, text: string) => {
    setPages(prev => prev.map(p => {
      if (p.localId !== pageLocalId) return p;
      return { ...p, questions: p.questions.map(q => {
        if (q.localId !== qLocalId) return q;
        return { ...q, answers: q.answers.map(a => a.localId === aLocalId ? { ...a, text } : a) };
      })};
    }));
  };

  const deleteAnswer = (pageLocalId: string, qLocalId: string, aLocalId: string) => {
    setPages(prev => prev.map(p => {
      if (p.localId !== pageLocalId) return p;
      return { ...p, questions: p.questions.map(q => {
        if (q.localId !== qLocalId) return q;
        return { ...q, answers: q.answers.filter(a => a.localId !== aLocalId) };
      })};
    }));
  };

  // ── Save ──────────────────────────────────────────────────────────────────────

  const handleSave = useCallback(async (publish = false) => {
    if (!title.trim()) { enqueueSnackbar('Course title is required', { variant: 'error' }); return; }
    setSaving(true);
    try {
      let cId = courseId;

      // 1. Create or update course
      const coursePayload = {
        title: title.trim(),
        description: description.trim(),
        passThreshold,
        retakeIntervalDays: retakeDays,
        isMandatory,
        isForAll,
        userIds: isForAll ? [] : assignedUserIds,
        isActive: publish,
      };

      if (!cId) {
        const { data } = await axios.post('training/course', coursePayload);
        cId = data.id;
        setCourseId(cId);
      } else {
        await axios.put(`training/course/${cId}`, coursePayload);
      }

      // 2. Save pages and questions
      for (let i = 0; i < pages.length; i++) {
        const pg = pages[i];
        if (pg.type === 'content') {
          const pagePayload = { courseId: cId, order: i + 1, title: pg.title, content: pg.content };
          if (!pg.serverId) {
            const { data } = await axios.post('training/page', pagePayload);
            setPages(prev => prev.map(p => p.localId === pg.localId ? { ...p, serverId: data.id } : p));
          } else {
            await axios.put(`training/page/${pg.serverId}`, { order: i + 1, title: pg.title, content: pg.content });
          }
        } else {
          // Quiz: save questions
          for (const q of pg.questions) {
            const qPayload = {
              courseId: cId, text: q.text, type: q.type,
              answers: q.answers.map(a => ({ text: a.text, isCorrect: a.isCorrect })),
              order: i + 1,
            };
            if (!q.serverId) {
              const { data } = await axios.post('training/question', qPayload);
              updateQuestion(pg.localId, q.localId, { serverId: data.id });
            } else {
              await axios.put(`training/question/${q.serverId}`, qPayload);
            }
          }
        }
      }

      setLastSaved(new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }));
      enqueueSnackbar(publish ? 'Course published!' : 'Course saved', { variant: 'success' });
      onSaved();
      if (publish) handleClose();
    } catch {
      enqueueSnackbar('Error saving course', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }, [courseId, title, description, passThreshold, retakeDays, isForAll, assignedUserIds, pages, onSaved, handleClose, enqueueSnackbar]);

  // ── Active page ───────────────────────────────────────────────────────────────

  const activePage = pages.find(p => p.localId === activeId) ?? null;
  const contentPages = pages.filter(p => p.type === 'content').length;
  const quizPages    = pages.filter(p => p.type === 'quiz').length;

  // ── Render helpers ────────────────────────────────────────────────────────────

  const renderSettings = () => (
    <div style={{ flex: 1, overflowY: 'auto', padding: '32px 40px', maxWidth: 720, width: '100%', margin: '0 auto' }}>
      <div style={{ fontSize: 20, fontWeight: 900, color: 'var(--text-color)', letterSpacing: '-.02em', marginBottom: 4 }}>Course Settings</div>
      <div style={{ fontSize: 13, color: 'var(--text-color-calendar-week)', marginBottom: 28 }}>Configure how this course appears to employees</div>

      {/* Icon */}
      <div style={{ marginBottom: 24 }}>
        <div className="cb-section-title"><span className="cb-section-dot" style={{ background: '#2563eb' }} />Course Icon</div>
        <div style={{ background: 'var(--btn-user-form)', border: '1px solid var(--border-input)', borderRadius: 14, overflow: 'hidden' }}>
          {/* Header: selected icon + expand/collapse toggle */}
          <button type="button"
            onClick={() => setShowIconGrid(v => !v)}
            style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: 14, background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, background: 'rgba(5,150,105,.12)', flexShrink: 0 }}>
              {emoji}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-color)' }}>Selected icon</div>
              <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>{showIconGrid ? 'Click to hide icons' : 'Click to choose an icon'}</div>
            </div>
            <span style={{ fontSize: 12, color: 'var(--text-color-calendar-week)', transform: showIconGrid ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>▾</span>
          </button>
          {/* Collapsible grid */}
          {showIconGrid && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '0 14px 14px', maxHeight: 260, overflowY: 'auto', borderTop: '1px solid var(--border-input)', paddingTop: 14 }}>
              {EMOJIS.map(e => (
                <button key={e} type="button"
                  onClick={() => setEmoji(e)}
                  style={{ width: 38, height: 38, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, cursor: 'pointer', border: emoji === e ? '2px solid #059669' : '2px solid transparent', background: emoji === e ? 'rgba(5,150,105,.12)' : 'transparent', transition: 'all .12s' }}>
                  {e}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Basic info */}
      <div style={{ marginBottom: 24 }}>
        <div className="cb-section-title"><span className="cb-section-dot" style={{ background: '#059669' }} />Basic Information</div>
        <div style={{ marginBottom: 14 }}>
          <label className="cb-label">Course title <span style={{ color: '#dc2626' }}>*</span></label>
          <input className="cb-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Fire Safety Awareness" />
        </div>
        <div>
          <label className="cb-label">Description</label>
          <textarea className="cb-input cb-textarea" value={description} onChange={e => setDescription(e.target.value)} placeholder="What will employees learn?" />
        </div>
      </div>

      {/* Rules */}
      <div style={{ marginBottom: 24 }}>
        <div className="cb-section-title"><span className="cb-section-dot" style={{ background: '#7c3aed' }} />Rules &amp; Requirements</div>
        <div className="cb-toggle-row" style={{ marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-color)' }}>Mandatory course</div>
            <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', marginTop: 2 }}>All assigned employees must complete this course</div>
          </div>
          <div className={`cb-tog${isMandatory ? ' on' : ' off'}`}
            onClick={() => setIsMandatory(v => !v)} />
        </div>
        <div style={{ marginBottom: 10 }}>
          <label className="cb-label">Pass threshold</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button type="button" className="cb-pass-btn" onClick={() => setPassThreshold(v => Math.max(10, v - 10))}>−</button>
            <span style={{ fontSize: 20, fontWeight: 900, color: '#7c3aed', minWidth: 50, textAlign: 'center' }}>{passThreshold}%</span>
            <button type="button" className="cb-pass-btn" onClick={() => setPassThreshold(v => Math.min(100, v + 10))}>+</button>
          </div>
        </div>
      </div>

      {/* Retake */}
      {(() => {
        const isCustom = retakeDays !== null && !RETAKE_OPTIONS.some(o => o.days === retakeDays);
        const applyCustom = (numStr: string, unit: 'days' | 'weeks' | 'months' | 'years') => {
          setCustomNum(numStr);
          setCustomUnit(unit);
          const n = parseFloat(numStr);
          if (!isNaN(n) && n > 0) setRetakeDays(Math.round(n * UNIT_DAYS[unit]));
          else setRetakeDays(null);
        };
        return (
          <div style={{ marginBottom: 24 }}>
            <div className="cb-section-title"><span className="cb-section-dot" style={{ background: '#d97706' }} />Retake Interval</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {RETAKE_OPTIONS.map(opt => (
                <button key={String(opt.days)} type="button"
                  className={`cb-chip${retakeDays === opt.days ? ' active' : ''}`}
                  onClick={() => { setRetakeDays(opt.days); setCustomNum(''); }}>
                  {opt.label}
                </button>
              ))}
              <button type="button"
                className={`cb-chip${isCustom ? ' active' : ''}`}
                onClick={() => applyCustom(customNum || '1', customUnit)}>
                Custom
              </button>
            </div>

            {isCustom && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
                <span style={{ fontSize: 13, color: 'var(--text-color-calendar-week)' }}>Retake every</span>
                <input
                  type="number"
                  title="Retake interval"
                  min={1}
                  className="cb-input"
                  value={customNum}
                  onChange={e => applyCustom(e.target.value, customUnit)}
                  style={{ width: 80 }}
                />
                <select
                  className="cb-input"
                  value={customUnit}
                  onChange={e => applyCustom(customNum || '1', e.target.value as 'days' | 'weeks' | 'months' | 'years')}
                  style={{ width: 'auto', cursor: 'pointer' }}
                >
                  <option value="days">days</option>
                  <option value="weeks">weeks</option>
                  <option value="months">months</option>
                  <option value="years">years</option>
                </select>
                <span style={{ fontSize: 12, color: 'var(--text-color-calendar-week)' }}>
                  ({retakeDays} days)
                </span>
              </div>
            )}
          </div>
        );
      })()}

      {/* Assign to */}
      <div style={{ marginBottom: 28 }}>
        <div className="cb-section-title"><span className="cb-section-dot" style={{ background: '#db2777' }} />Assign To</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[{ label: 'All employees', val: true }, { label: 'Specific users', val: false }].map(opt => (
            <button key={String(opt.val)} type="button"
              className={`cb-chip${isForAll === opt.val ? ' active' : ''}`}
              onClick={() => setIsForAll(opt.val)}>
              {opt.label}
            </button>
          ))}
        </div>
        {!isForAll && (
          <div style={{ marginTop: 12 }}>
            <label className="cb-label">Select employees to assign</label>
            <SelectUsers selectedTasks={assignedUserIds} setSelectedTasks={setAssignedUserIds} />
            <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', marginTop: 6 }}>
              {assignedUserIds.length > 0
                ? `${assignedUserIds.length} employee${assignedUserIds.length !== 1 ? 's' : ''} selected`
                : 'No employees selected yet — this course will not be visible to anyone.'}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button type="button" className="cb-btn-ghost" onClick={handleClose}>Cancel</button>
        <button type="button" className="cb-btn-primary" onClick={() => handleSave(false)} disabled={saving}>
          {saving ? 'Saving…' : 'Save settings'}
        </button>
      </div>
    </div>
  );

  const renderContentEditor = (pg: PageDraft) => {
    const pgIdx = pages.findIndex(p => p.localId === pg.localId);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
        {/* Type label row */}
        <div className="cb-editor-label-row">
          <div className="cb-editor-type-badge">
            <span className="cb-editor-type-dot" style={{ background: '#3b82f6' }} />
            Content page
          </div>
          <span style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', marginLeft: 4 }}>Use toolbar to format content</span>
        </div>

        {/* Page title with separator */}
        <div className="cb-editor-title-area">
          <input
            className="cb-page-title-input"
            value={pg.title}
            onChange={e => updatePage(pg.localId, { title: e.target.value })}
            placeholder="Page title…"
          />
        </div>

        {/* RichEditor card */}
        <div className="cb-editor-frame">
          <RichEditor
            value={pg.content}
            onChange={val => updatePage(pg.localId, { content: val })}
            placeholder="Start writing your page content here…"
            fillHeight
            baseUrl={baseUrl}
            uploadPath="training/upload-image"
          />
        </div>

        {/* Nav footer */}
        <div className="cb-editor-nav">
          <span style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>
            📄 Page {pgIdx + 1} of {pages.length}
          </span>
          <div style={{ flex: 1 }} />
          {pgIdx > 0 && (
            <button type="button" className="cb-btn-ghost" style={{ fontSize: 11, padding: '5px 12px' }}
              onClick={() => setActiveId(pages[pgIdx - 1].localId)}>
              ← Previous
            </button>
          )}
          {pgIdx < pages.length - 1 && (
            <button type="button" className="cb-btn-primary" style={{ fontSize: 11, padding: '5px 14px' }}
              onClick={() => setActiveId(pages[pgIdx + 1].localId)}>
              Next →
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderQuizEditor = (pg: PageDraft) => {
    const q = pg.questions[0];
    if (!q) return null;
    return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '32px 44px' }}>
      {/* Quiz label */}
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: '#7c3aed', display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
        <span style={{ background: 'rgba(124,58,237,.1)', padding: '3px 9px', borderRadius: 6 }}>✦ Quiz question</span>
      </div>
      {/* Question text — large input like page title */}
      <input
        className="cb-page-title-input"
        value={q.text}
        onChange={e => updateQuestion(pg.localId, q.localId, { text: e.target.value })}
        placeholder="Type your question here…"
        style={{ marginBottom: 24, paddingBottom: 18, borderBottom: '1px dashed var(--border-input)' }}
      />

      {/* Answer-type toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 4, background: 'var(--btn-user-form)', border: '1px solid var(--border-input)', borderRadius: 8, padding: 3 }}>
          {([
            { v: 'single',   l: '○ Single answer' },
            { v: 'multiple', l: '☑ Multiple answers' },
          ] as { v: QuestionDraft['type']; l: string }[]).map(opt => {
            const active = (q.type === 'multiple') === (opt.v === 'multiple');
            return (
              <button key={opt.v} type="button"
                onClick={() => setQuestionType(pg.localId, q.localId, opt.v)}
                style={{
                  fontSize: 11, fontWeight: 700, padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
                  background: active ? '#7c3aed' : 'transparent',
                  color: active ? '#fff' : 'var(--text-color-calendar-week)',
                  transition: 'all .12s',
                }}>
                {opt.l}
              </button>
            );
          })}
        </div>
        <span style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>
          {q.type === 'multiple' ? 'Tick all correct options' : 'Pick the one correct option'}
        </span>
      </div>

      {/* Answer options */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
        {q.answers.map(a => (
          <div key={a.localId}
            className={`cb-option${a.isCorrect ? ' correct' : ''}`}
            onClick={() => toggleCorrect(pg.localId, q.localId, a.localId)}>
            <div className={`cb-option-radio ${q.type === 'multiple' ? 'cb-radio-check' : 'cb-radio-dot'}`}>
              {a.isCorrect && q.type === 'multiple' && (
                <span style={{ fontSize: 11, color: 'white', fontWeight: 900, lineHeight: 1 }}>✓</span>
              )}
              {a.isCorrect && q.type !== 'multiple' && (
                <span className="cb-radio-inner-dot" />
              )}
            </div>
            <input
              type="text" className="cb-option-input"
              value={a.text}
              onChange={e => updateAnswer(pg.localId, q.localId, a.localId, e.target.value)}
              onClick={e => e.stopPropagation()}
              placeholder={`Option ${q.answers.indexOf(a) + 1}…`}
            />
            {a.isCorrect && <span style={{ fontSize: 9, fontWeight: 800, color: '#059669', background: 'rgba(5,150,105,.12)', padding: '2px 8px', borderRadius: 6, flexShrink: 0 }}>✓ Correct</span>}
            <button type="button" className="cb-opt-del"
              onClick={e => { e.stopPropagation(); deleteAnswer(pg.localId, q.localId, a.localId); }}>✕</button>
          </div>
        ))}
      </div>
      <button type="button" className="cb-add-option" onClick={() => addOption(pg.localId, q.localId)}>+ Add option</button>

      {/* Add next question shortcut */}
      <button type="button" className="cb-add-question" style={{ marginTop: 28 }} onClick={() => addQuestion(pg.localId)}>
        <span style={{ fontSize: 20, fontWeight: 900 }}>+</span> Add next question
      </button>
    </div>
    );
  };

  // ── Main render ───────────────────────────────────────────────────────────────

  const panel = (
    <div className={`cb-overlay${open ? ' open' : ''}`}>

      {/* Top bar */}
      <div className="cb-topbar">
        <button type="button" className="cb-back-btn" onClick={handleClose}>← Training</button>
        <div className="cb-topbar-div" />
        <input
          type="text"
          className="cb-title-input"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="Untitled course…"
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-color-calendar-week)', flexShrink: 0 }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: lastSaved ? '#10b981' : 'var(--border-input)' }} />
          {lastSaved ? `Saved · ${lastSaved}` : 'Unsaved'}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <button type="button" className="cb-btn-ghost" onClick={() => handleSave(false)} disabled={saving}>
            {saving ? '…' : 'Save draft'}
          </button>
          <button type="button" className="cb-btn-publish" onClick={() => handleSave(true)} disabled={saving}>
            Publish
          </button>
        </div>
      </div>

      {/* Builder body */}
      <div className="cb-builder">

        {/* Sidebar */}
        <aside className="cb-sidebar">
          {/* Course meta */}
          <div style={{ padding: '12px 14px 10px', borderBottom: '1px solid var(--border-input)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <button type="button"
                style={{ width: 44, height: 44, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, cursor: 'pointer', border: '1.5px dashed var(--border-input)', background: 'var(--btn-user-form)', flexShrink: 0 }}
                onClick={() => setShowEmojiPicker(e => !e)}>
                {emoji}
              </button>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: isMandatory ? '#dc2626' : '#059669', marginBottom: 2 }}>
                  {isMandatory ? '⚠ Mandatory' : '📂 Optional'}
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)' }}>
                  {contentPages} page{contentPages !== 1 ? 's' : ''} · {quizPages} question{quizPages !== 1 ? 's' : ''}
                </div>
              </div>
            </div>
            {showEmojiPicker && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: 8, background: 'var(--bg-form)', border: '1px solid var(--border-input)', borderRadius: 10, marginTop: 4, maxHeight: 200, overflowY: 'auto' }}>
                {EMOJIS.map(e => (
                  <button key={e} type="button"
                    onClick={() => { setEmoji(e); setShowEmojiPicker(false); }}
                    style={{ width: 32, height: 32, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, cursor: 'pointer', border: emoji === e ? '2px solid #059669' : 'none', background: emoji === e ? 'rgba(5,150,105,.1)' : 'transparent' }}>
                    {e}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Settings nav */}
          <div style={{ padding: '6px 8px 2px' }}>
            <button type="button"
              className={`cb-nav-btn${activePanel === 'settings' ? ' active' : ''}`}
              onClick={() => { setActivePanel('settings'); setActiveId(null); }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(37,99,235,.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>⚙️</div>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-color-calendar)' }}>Course settings</span>
            </button>
          </div>

          <div style={{ padding: '6px 16px 4px', fontSize: 9, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)' }}>Content</div>

          {/* Pages list */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '4px 8px' }}>
            {pages.map((pg, i) => {
              const isActive = activeId === pg.localId;
              const isDragging = dragIdx === i;
              const isDropTarget = dropIdx === i && dragIdx !== i;
              const displayTitle = pg.type === 'quiz'
                ? (pg.questions[0]?.text?.slice(0, 36) || 'New question') + ((pg.questions[0]?.text?.length ?? 0) > 36 ? '…' : '')
                : (pg.title || 'Untitled page');
              return (
                <div key={pg.localId}
                  draggable
                  onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; setDragIdx(i); }}
                  onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDropIdx(i); }}
                  onDrop={e => {
                    e.preventDefault();
                    if (dragIdx !== null && dragIdx !== i) {
                      setPages(prev => {
                        const arr = [...prev];
                        const [moved] = arr.splice(dragIdx, 1);
                        arr.splice(i, 0, moved);
                        return arr;
                      });
                    }
                    setDragIdx(null); setDropIdx(null);
                  }}
                  onDragEnd={() => { setDragIdx(null); setDropIdx(null); }}
                  className={`cb-page-item${isActive ? ' active' : ''}${isDropTarget ? ' drag-over' : ''}${isDragging ? ' dragging' : ''}`}
                  onClick={() => { setActiveId(pg.localId); setActivePanel('page'); }}>
                  {/* Drag handle */}
                  <span className="cb-drag-handle">⠿</span>
                  {/* Number / type icon */}
                  <div style={{ width: 20, height: 20, borderRadius: 6, border: `1.5px solid ${isActive ? (pg.type === 'quiz' ? '#7c3aed' : '#059669') : 'var(--border-input)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 800, color: isActive ? (pg.type === 'quiz' ? '#7c3aed' : '#059669') : 'var(--text-color-calendar-week)', flexShrink: 0 }}>
                    {pg.type === 'quiz' ? '✦' : i + 1}
                  </div>
                  {/* Title */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: isActive ? (pg.type === 'quiz' ? '#7c3aed' : '#059669') : 'var(--text-color-calendar)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {displayTitle}
                    </div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: pg.type === 'quiz' ? '#7c3aed' : 'var(--text-color-calendar-week)', marginTop: 1 }}>
                      {pg.type === 'quiz' ? '✦ Quiz question' : '📄 Content'}
                    </div>
                  </div>
                  <button type="button"
                    style={{ width: 20, height: 20, borderRadius: 5, border: 'none', background: 'transparent', color: 'var(--text-color-calendar-week)', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'all .12s' }}
                    className="cb-page-del"
                    onClick={e => { e.stopPropagation(); deletePage(pg.localId); }}>✕</button>
                </div>
              );
            })}
          </div>

          {/* Add page */}
          <div style={{ padding: '6px 8px 8px', position: 'relative' }} ref={addMenuRef}>
            <button type="button" className="cb-add-page-btn" onClick={() => setShowAddMenu(m => !m)}>
              <span style={{ fontSize: 16, fontWeight: 900 }}>+</span> Add page
            </button>
            {showAddMenu && (
              <div className="cb-add-menu open">
                <div className="cb-add-menu-item" onClick={() => addPage('content')}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(37,99,235,.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15 }}>📄</div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-color)' }}>Content page</div>
                    <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', marginTop: 1 }}>Text, images, callouts</div>
                  </div>
                </div>
                <div className="cb-add-menu-item" onClick={() => addPage('quiz')}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(124,58,237,.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15 }}>✦</div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-color)' }}>Quiz / Assessment</div>
                    <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', marginTop: 1 }}>Multiple choice questions</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer progress */}
          <div style={{ padding: '8px 12px', borderTop: '1px solid var(--border-input)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
              <span style={{ fontSize: 10, color: 'var(--text-color-calendar-week)' }}>Course completion</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#059669' }}>{pages.length > 0 ? `${pages.length} page${pages.length !== 1 ? 's' : ''}` : 'No pages yet'}</span>
            </div>
            <div style={{ height: 4, borderRadius: 2, background: 'var(--border-input)', overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: 2, background: 'linear-gradient(90deg,#059669,#34d399)', width: `${pages.length > 0 ? Math.min(100, (pages.filter(p => p.title).length / pages.length) * 100) : 0}%`, transition: 'width .4s' }} />
            </div>
          </div>
        </aside>

        {/* Editor area */}
        <div className="cb-editor">
          {activePanel === 'settings' && renderSettings()}
          {activePanel === 'page' && activePage && (
            activePage.type === 'content' ? renderContentEditor(activePage) : renderQuizEditor(activePage)
          )}
          {activePanel === 'page' && !activePage && (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, color: 'var(--text-color-calendar-week)' }}>
              <div style={{ fontSize: 32, opacity: .4 }}>📄</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>Select a page from the sidebar</div>
            </div>
          )}
        </div>

      </div>

      {/* Bottom bar */}
      <div className="cb-bottom">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {[`📄 ${contentPages} pages`, `✦ ${quizPages} quizzes`, `👥 ${isForAll ? 'All employees' : 'Selected users'}`].map(s => (
            <div key={s} style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', display: 'flex', alignItems: 'center', gap: 5 }}>{s}</div>
          ))}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>
          {lastSaved ? `✓ All changes saved · ${lastSaved}` : 'Unsaved changes'}
        </div>
      </div>

    </div>
  );

  return createPortal(panel, document.body);
};

export default CourseBuilder;
